import React from 'react';

import spacingTokens from '../../../spacing/tokens.module.scss';

import styles from './Stack.module.scss';

/*
Source: https://every-layout.dev/layouts/stack/
Example:
<Stack space="spacing-md">
  <h2>Header</h2>
  <img src={logo} className="App-logo" />
  <p>Paragraph</p>
</Stack>
*/

export type SpacingValue = keyof typeof spacingTokens;

export type SpacingToken = `${SpacingValue}`;

export interface StackProps {
    space?: SpacingToken;
}

/**
 *
 * The `Stack` component uses spacing tokens to set the spacing between its child elements. The following spacing tokens are available:
 *
 * > ** spacing-none ** = 0px
 *
 * > ** spacing-xxs ** = 4px
 *
 * > ** spacing-xs ** = 8px
 *
 * > ** spacing-sm ** = 12px
 *
 * > ** spacing-md ** = 16px
 *
 * > ** spacing-lg ** = 24px
 *
 * > ** spacing-xl ** = 32px
 *
 * > ** spacing-xxl ** = 40px
 *
 *
 * #### Developer guidelines:
 *
 * - Use the `space` prop to specify the spacing between the child elements, using one of the spacing tokens listed above.
 * - The Stack component should only be used to space its immediate child elements, and not to apply global layout styles to the entire page.
 *
 */
export const Stack: React.FC<StackProps> = ({ space = 'spacing-none', children }) => (
    <div
        className={styles.root}
        style={
            {
                '--Space': spacingTokens[space] || '4px',
            } as React.CSSProperties
        }
    >
        {children}
    </div>
);
